/**
 * True only for development builds. Used to gate the client-side module
 * fallbacks below so production navigation reflects strictly the gateway's
 * (entitlement-filtered) module list. Read defensively — the shell isn't
 * compiled with `vite/client` ambient types, so `import.meta.env` is typed
 * loosely here.
 */
export const isDevBuild: boolean = Boolean(
  (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV
);

/**
 * Modules whose federated bundle exists client-side but whose backend
 * manifest entry isn't shipped yet (CRM/Finance/HR/Sales/Multisite are
 * v2 module surfaces still being plumbed through the gateway). In a
 * development build the shell synthesises manifest entries for these so
 * they still appear in the sidebar / browse / palette and their views load
 * via federation. These are NEVER injected in production, where the nav
 * must reflect only the gateway-entitled module list.
 *
 * When the gateway eventually ships entries for these, the gateway data
 * wins (Pass 2 federated upgrade replaces stubs the same way it does for
 * shipped modules).
 */
export const CLIENT_FALLBACK_MODULES = [
  {
    moduleId: 'tyndo.crm',
    name: 'CRM',
    federatedModuleName: 'crm_ui',
    icon: 'Users',
    menus: [
      { menuId: 'tyndo.crm.root', label: 'CRM', sequence: 100, icon: 'Users' },
    ],
    views: [],
    actions: [],
  },
  {
    moduleId: 'tyndo.finance',
    name: 'Finance',
    federatedModuleName: 'finance_ui',
    icon: 'Banknote',
    menus: [
      { menuId: 'tyndo.finance.root', label: 'Finance', sequence: 100, icon: 'Banknote' },
    ],
    views: [],
    actions: [],
  },
  {
    moduleId: 'tyndo.hr',
    name: 'HR',
    federatedModuleName: 'hr_ui',
    icon: 'UserCog',
    menus: [
      { menuId: 'tyndo.hr.root', label: 'HR', sequence: 100, icon: 'UserCog' },
    ],
    views: [],
    actions: [],
  },
  {
    moduleId: 'tyndo.sales',
    name: 'Sales',
    federatedModuleName: 'sales_ui',
    icon: 'TrendingUp',
    menus: [
      { menuId: 'tyndo.sales.root', label: 'Sales', sequence: 100, icon: 'TrendingUp' },
    ],
    views: [],
    actions: [],
  },
  {
    moduleId: 'tyndo.multisite',
    name: 'Multisite',
    federatedModuleName: 'multisite_ui',
    icon: 'Globe2',
    menus: [
      { menuId: 'tyndo.multisite.root', label: 'Multisite', sequence: 100, icon: 'Globe2' },
    ],
    views: [],
    actions: [],
  },
  {
    moduleId: 'tyndo.plm',
    name: 'PLM',
    federatedModuleName: 'plm_ui',
    icon: 'Layers',
    menus: [
      { menuId: 'tyndo.plm.root', label: 'PLM', sequence: 100, icon: 'Layers' },
    ],
    views: [],
    actions: [],
  },
  {
    moduleId: 'tyndo.digital-twin',
    name: 'Digital Twin',
    federatedModuleName: 'digital_twin_ui',
    icon: 'Cpu',
    menus: [
      { menuId: 'tyndo.digital-twin.root', label: 'Digital Twin', sequence: 150, icon: 'Cpu' },
    ],
    views: [],
    actions: [],
  },
  {
    // Controls (protocol-agnostic supervisory device management) UI — the
    // federated bundle ships device list / console / onboarding wizard /
    // redundancy management but the gateway manifest doesn't list it yet.
    moduleId: 'tyndo.controls',
    name: 'Controls',
    federatedModuleName: 'controls_ui',
    icon: 'Cpu',
    menus: [
      { menuId: 'tyndo.controls.root', label: 'Controls', sequence: 82, icon: 'Cpu' },
    ],
    views: [],
    actions: [],
  },
  {
    moduleId: 'tyndo.designer',
    name: 'Designer',
    federatedModuleName: 'designer_ui',
    icon: 'PenTool',
    menus: [
      { menuId: 'tyndo.designer.root', label: 'Designer', sequence: 160, icon: 'PenTool' },
    ],
    views: [],
    actions: [],
  },
  {
    moduleId: 'tyndo.marketplace',
    name: 'Marketplace',
    federatedModuleName: 'marketplace_ui',
    icon: 'Store',
    menus: [
      { menuId: 'tyndo.marketplace.root', label: 'Marketplace', sequence: 220, icon: 'Store' },
    ],
    views: [],
    actions: [],
  },
  {
    moduleId: 'tyndo.sustainability',
    name: 'Sustainability',
    federatedModuleName: 'sustainability_ui',
    icon: 'Leaf',
    menus: [
      { menuId: 'tyndo.sustainability.root', label: 'Sustainability', sequence: 230, icon: 'Leaf' },
    ],
    views: [],
    actions: [],
  },
  {
    // Edge Fleet management (node health / heartbeats / OTA deployments) —
    // the compiled-in bundle ships the fleet console + deployment console.
    moduleId: 'tyndo.edgefleet',
    name: 'Edge Fleet',
    federatedModuleName: 'edgefleet_ui',
    icon: 'Network',
    menus: [
      { menuId: 'tyndo.edgefleet.root', label: 'Edge Fleet', sequence: 84, icon: 'Network' },
    ],
    views: [],
    actions: [],
  },
  {
    moduleId: 'tyndo.collaboration',
    name: 'Collaboration',
    federatedModuleName: 'collaboration_ui',
    icon: 'Users',
    menus: [
      { menuId: 'tyndo.collaboration.root', label: 'Collaboration', sequence: 240, icon: 'Users' },
    ],
    views: [],
    actions: [],
  },
  // UI-only platform surfaces (no backend module). Compiled into the shell as
  // lazy chunks (COMPILED_IN_MODULE_NAMES) but with no gateway backend, so a
  // gateway-less dev build needs these fallback stubs to surface them in the
  // sidebar / browse / palette. Icon + label + sequence mirror each module's
  // own src/index.ts root menu; Pass 2 replaces the stub with the real tree.
  {
    moduleId: 'tyndo.teaming',
    name: 'Teaming',
    federatedModuleName: 'teaming_ui',
    icon: 'RadioTower',
    menus: [
      { menuId: 'tyndo.teaming.root', label: 'Teaming', sequence: 79, icon: 'RadioTower' },
    ],
    views: [],
    actions: [],
  },
  {
    moduleId: 'tyndo.spatial',
    name: 'Spatial',
    federatedModuleName: 'spatial_ui',
    icon: 'Boxes',
    menus: [
      { menuId: 'tyndo.spatial.root', label: 'Spatial', sequence: 80, icon: 'Boxes' },
    ],
    views: [],
    actions: [],
  },
  {
    moduleId: 'tyndo.authoring',
    name: 'Authoring',
    federatedModuleName: 'authoring_ui',
    icon: 'Wand2',
    menus: [
      { menuId: 'tyndo.authoring.root', label: 'Authoring', sequence: 80, icon: 'Wand2' },
    ],
    views: [],
    actions: [],
  },
  {
    moduleId: 'tyndo.voice',
    name: 'Voice',
    federatedModuleName: 'voice_ui',
    icon: 'Mic',
    menus: [
      { menuId: 'tyndo.voice.root', label: 'Voice', sequence: 81, icon: 'Mic' },
    ],
    views: [],
    actions: [],
  },
  {
    moduleId: 'tyndo.lineage',
    name: 'Lineage',
    federatedModuleName: 'lineage_ui',
    icon: 'GitFork',
    menus: [
      { menuId: 'tyndo.lineage.root', label: 'Lineage', sequence: 80, icon: 'GitFork' },
    ],
    views: [],
    actions: [],
  },
  {
    // Query Visa (consented compute-to-data governance) console — lists issued
    // visas + scope/expiry/holder and each visa's execution audit trail.
    moduleId: 'tyndo.queryvisa',
    name: 'Query Visa',
    federatedModuleName: 'queryvisa_ui',
    icon: 'KeyRound',
    menus: [
      { menuId: 'tyndo.queryvisa.root', label: 'Query Visa', sequence: 83, icon: 'KeyRound' },
    ],
    views: [],
    actions: [],
  },
  {
    // Transparency Log (shared Merkle notary) — shows the latest signed tree
    // head and verifies a tree head's commitment stamp.
    moduleId: 'tyndo.transparencylog',
    name: 'Transparency Log',
    federatedModuleName: 'transparencylog_ui',
    icon: 'ScrollText',
    menus: [
      { menuId: 'tyndo.transparencylog.root', label: 'Transparency Log', sequence: 84, icon: 'ScrollText' },
    ],
    views: [],
    actions: [],
  },
  {
    // Causal Attribution (Batch Autopsy) — a graph-walked do-calculus RCA
    // console: name a batch/lot/serial + outcome, rank the identified causes.
    moduleId: 'tyndo.causalattribution',
    name: 'Causal Attribution',
    federatedModuleName: 'causalattribution_ui',
    icon: 'GitFork',
    menus: [
      { menuId: 'tyndo.causalattribution.root', label: 'Causal Autopsy', sequence: 86, icon: 'GitFork' },
    ],
    views: [],
    actions: [],
  },
  {
    // PCF Wire (product carbon footprint) console — lists live PCF credentials
    // and carbon bonds with their primary/estimated/unknown fraction band.
    moduleId: 'tyndo.pcfwire',
    name: 'PCF Wire',
    federatedModuleName: 'pcfwire_ui',
    icon: 'Leaf',
    menus: [
      { menuId: 'tyndo.pcfwire.root', label: 'PCF Wire', sequence: 85, icon: 'Leaf' },
    ],
    views: [],
    actions: [],
  },
  {
    // Historian Trend Explorer — tag-picked trends with the server-computed
    // SPC overlay and the regulated annotation review workflow.
    moduleId: 'tyndo.historian',
    name: 'Historian',
    federatedModuleName: 'historian_ui',
    icon: 'LineChart',
    menus: [
      { menuId: 'tyndo.historian.root', label: 'Historian', sequence: 86, icon: 'LineChart' },
    ],
    views: [],
    actions: [],
  },
  {
    // Unified Namespace Browser — the canonical ISA-95 hierarchy tree with
    // per-node protocol bindings/validation and the tag-reconciliation queue.
    moduleId: 'tyndo.uns',
    name: 'Unified Namespace',
    federatedModuleName: 'uns_ui',
    icon: 'FolderTree',
    menus: [
      { menuId: 'tyndo.uns.root', label: 'Unified Namespace', sequence: 87, icon: 'FolderTree' },
    ],
    views: [],
    actions: [],
  },
  {
    // Write Firewall Console — the semantic OT write firewall: dry-run/live
    // write evaluation, the held two-person approval queue, the authored tag
    // write policies, and the tamper-evident decision log + chain verify.
    moduleId: 'tyndo.writefirewall',
    name: 'Write Firewall',
    federatedModuleName: 'writefirewall_ui',
    icon: 'ShieldAlert',
    menus: [
      { menuId: 'tyndo.writefirewall.root', label: 'Write Firewall', sequence: 88, icon: 'ShieldAlert' },
    ],
    views: [],
    actions: [],
  },
  {
    // Blackout Outbox Console — offline-write survivability: the queued governed
    // writes (all / still-queued), a selected write's payload + attribution + a
    // live content-digest verify, and the at-most-once reconnect replay history.
    moduleId: 'tyndo.blackoutoutbox',
    name: 'Blackout Outbox',
    federatedModuleName: 'blackoutoutbox_ui',
    icon: 'CloudOff',
    menus: [
      { menuId: 'tyndo.blackoutoutbox.root', label: 'Blackout Outbox', sequence: 87, icon: 'CloudOff' },
    ],
    views: [],
    actions: [],
  },
  {
    // Blackboard Console — the governed causal plant-context blackboard: the
    // live context assertions (fact + provenance + calibrated confidence), a
    // selected assertion's causal neighborhood + full version history + any
    // conflicting live versions, and the governed publish/supersede/link ops.
    moduleId: 'tyndo.blackboard',
    name: 'Blackboard',
    federatedModuleName: 'blackboard_ui',
    icon: 'Braces',
    menus: [
      { menuId: 'tyndo.blackboard.root', label: 'Blackboard', sequence: 87, icon: 'Braces' },
    ],
    views: [],
    actions: [],
  },
  {
    // Capacity Exchange Console — the verified capacity exchange: the open
    // schedule-certified listings board, the governed list/bid/clear/withdraw
    // operations, and the settlement-record lookup + digest tamper check.
    moduleId: 'tyndo.capacityexchange',
    name: 'Capacity Exchange',
    federatedModuleName: 'capacityexchange_ui',
    icon: 'ArrowLeftRight',
    menus: [
      { menuId: 'tyndo.capacityexchange.root', label: 'Capacity Exchange', sequence: 89, icon: 'ArrowLeftRight' },
    ],
    views: [],
    actions: [],
  },
  {
    // Passport Console — the worker-owned competence passport: the by-id
    // credential lookup + server-side digest tamper verification, the governed
    // issue over really-earned qualifications (422 refusal without evidence),
    // the cross-org fail-closed eligibility check, and the governed revoke.
    moduleId: 'tyndo.competencepassport',
    name: 'Competence Passport',
    federatedModuleName: 'competencepassport_ui',
    icon: 'BadgeCheck',
    menus: [
      { menuId: 'tyndo.competencepassport.root', label: 'Competence Passport', sequence: 90, icon: 'BadgeCheck' },
    ],
    views: [],
    actions: [],
  },
  {
    // Network Console — the audit-once qualification network: the by-supplier
    // computed-qualification lookup + governed digest-chain tamper check, the
    // governed recompute over live quality metrics (computed, never asserted),
    // the consented subscription, and the fail-closed disclosed feed.
    moduleId: 'tyndo.qualificationnetwork',
    name: 'Qualification Network',
    federatedModuleName: 'qualificationnetwork_ui',
    icon: 'Share2',
    menus: [
      { menuId: 'tyndo.qualificationnetwork.root', label: 'Qualification Network', sequence: 91, icon: 'Share2' },
    ],
    views: [],
    actions: [],
  },
  {
    // Exchange Console — the verified seconds exchange: the open listings
    // board over real failing quality records, the governed
    // list/reserve/transfer-custody/withdraw operations, the dossier tamper
    // check, and the buyer's sealed post-transfer provenance read.
    moduleId: 'tyndo.secondsexchange',
    name: 'Seconds Exchange',
    federatedModuleName: 'secondsexchange_ui',
    icon: 'Recycle',
    menus: [
      { menuId: 'tyndo.secondsexchange.root', label: 'Seconds Exchange', sequence: 92, icon: 'Recycle' },
    ],
    views: [],
    actions: [],
  },
  {
    // Underwriting Console — the autonomy underwriting pack: the insurer's
    // fail-closed disclosed read of the redacted safety case + digest-chained
    // feed, the tenant-side digest tamper verification, the governed compose
    // over real governance posture, and the insurer grant with
    // insurer-attested consent.
    moduleId: 'tyndo.underwritingpack',
    name: 'Underwriting Pack',
    federatedModuleName: 'underwritingpack_ui',
    icon: 'FileText',
    menus: [
      { menuId: 'tyndo.underwritingpack.root', label: 'Underwriting Pack', sequence: 93, icon: 'FileText' },
    ],
    views: [],
    actions: [],
  },
  {
    // Digital Product Passport console — issue + inspect genealogy-sealed,
    // selectively-disclosable EU-DPP/EPCIS-2.0 product credentials (honest
    // carbon/recipe/quality composition, content seal, transparency-log anchor).
    moduleId: 'tyndo.productpassport',
    name: 'Product Passport',
    federatedModuleName: 'productpassport_ui',
    icon: 'ShieldCheck',
    menus: [
      { menuId: 'tyndo.productpassport.root', label: 'Product Passport', sequence: 94, icon: 'ShieldCheck' },
    ],
    views: [],
    actions: [],
  },
  {
    // Handshake Ledger Console — the bilateral handshake ledger: open + sign an
    // offer, complete the mutual attestation on accept (or decline), verify the
    // tamper-evident chain, and run signed multilateral nettings that collapse
    // gross positions to zero-sum nets.
    moduleId: 'tyndo.handshakeledger',
    name: 'Handshake Ledger',
    federatedModuleName: 'handshakeledger_ui',
    icon: 'Handshake',
    menus: [
      { menuId: 'tyndo.handshakeledger.root', label: 'Handshake Ledger', sequence: 95, icon: 'Handshake' },
    ],
    views: [],
    actions: [],
  },
  {
    // Digest Treaty Console — the governed inter-company digest treaty: propose
    // opaque deal terms (the module computes their stable canonical digest),
    // gather each side's signed governance approval of that byte-identical
    // digest, bind only on a genuine two-sided match, and verify the
    // tamper-evident, non-repudiable bound transcript.
    moduleId: 'tyndo.digesttreaty',
    name: 'Digest Treaty',
    federatedModuleName: 'digesttreaty_ui',
    icon: 'FileSignature',
    menus: [
      { menuId: 'tyndo.digesttreaty.root', label: 'Digest Treaty', sequence: 96, icon: 'FileSignature' },
    ],
    views: [],
    actions: [],
  },
  {
    // Federated Work Order Console — route a work order + its recipe to a
    // counterparty enterprise (governed, fail-closed), track its accept/reject
    // lifecycle, reconcile the genealogy + eBR evidence that flows back on the
    // same spine, complete only once reconciled evidence exists, and verify the
    // routing + evidence digest chain.
    moduleId: 'tyndo.federatedworkorder',
    name: 'Federated Work Orders',
    federatedModuleName: 'federatedworkorder_ui',
    icon: 'Network',
    menus: [
      { menuId: 'tyndo.federatedworkorder.root', label: 'Federated Work Orders', sequence: 96, icon: 'Network' },
    ],
    views: [],
    actions: [],
  },
  {
    // Motion Genome Console — the co-keyed hand-skill genome: record expert +
    // trainee motion captures from the edge, fold the experts into the golden
    // motion envelope per (step, station), coach a trainee against it with honest
    // per-segment deltas, and transfer an established skill across sites as a
    // signed certificate. A plant-floor / robotics hand-skill surface.
    moduleId: 'tyndo.motiongenome',
    name: 'Motion Genome',
    federatedModuleName: 'motiongenome_ui',
    icon: 'Activity',
    menus: [
      { menuId: 'tyndo.motiongenome.root', label: 'Motion Genome', sequence: 85, icon: 'Activity' },
    ],
    views: [],
    actions: [],
  },
  {
    // Plant Sandbox Console — the counterparty virtual-plant sandbox: provision
    // a hardware-free, production-isolated replica of the plant, run signed
    // conformance certifications a counterparty can trust (Certified only when
    // every required check passes), verify a run's signed digest, and revoke a
    // sandbox fail-closed.
    moduleId: 'tyndo.plantsandbox',
    name: 'Plant Sandbox',
    federatedModuleName: 'plantsandbox_ui',
    icon: 'FlaskConical',
    menus: [
      { menuId: 'tyndo.plantsandbox.root', label: 'Plant Sandbox', sequence: 97, icon: 'FlaskConical' },
    ],
    views: [],
    actions: [],
  },
  {
    // Response Atlas Console — the learned CPP→CQA response book: browse the
    // transfer-function priors, look one up with an honest cold start, predict at
    // a CPP value with not-identifiable / out-of-range flags, verify a prior's
    // tamper-evidence, fold governed observation batches, and contribute to the
    // consent-gated consortium as sufficient statistics only. A Level-3
    // process-intelligence surface over the plant's response knowledge.
    moduleId: 'tyndo.responseatlas',
    name: 'Response Atlas',
    federatedModuleName: 'responseatlas_ui',
    icon: 'Activity',
    menus: [
      { menuId: 'tyndo.responseatlas.root', label: 'Response Atlas', sequence: 88, icon: 'Activity' },
    ],
    views: [],
    actions: [],
  },
  {
    // Flight Simulator Console — the operator flight simulator: define governed,
    // signed training scenarios over REAL recorded trajectories + ordered
    // injected faults, run graded operator attempts that certify only on an
    // honest pass (every required fault handled within tolerance), verify a run's
    // signed digest, and read an operator's certification record (only genuinely
    // Passed, certified attempts). An operator-training surface that runs the line.
    moduleId: 'tyndo.flightsimulator',
    name: 'Flight Simulator',
    federatedModuleName: 'flightsimulator_ui',
    icon: 'Plane',
    menus: [
      { menuId: 'tyndo.flightsimulator.root', label: 'Flight Simulator', sequence: 89, icon: 'Plane' },
    ],
    views: [],
    actions: [],
  },
];

/**
 * Resolve the optional fallback-module allowlist from the build environment.
 * `TYNDO_FALLBACK_MODULES` is a comma-separated list of moduleIds; when set, the
 * dev fallback is restricted to exactly those ids (so a team can run with only a
 * subset wired client-side). Returns null when unset — meaning "no restriction,
 * use the full built-in list". Read defensively because the shell isn't compiled
 * with ambient `import.meta.env` types.
 */
export function resolveFallbackModuleAllowlist(): Set<string> | null {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  const raw = (env?.TYNDO_FALLBACK_MODULES ?? '').trim();
  if (!raw) return null;
  const ids = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return ids.length ? new Set(ids) : null;
}
